import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  CircularProgress,
  useTheme,
  useMediaQuery,
  IconButton,
  Modal
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import DownloadIcon from '@mui/icons-material/Download';
import HistoryIcon from '@mui/icons-material/History';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'notistack';
import enLocale from 'date-fns/locale/en-GB';
import { fetchHistoricalTrades } from '../../../services/backendAPI';
import { formatApiDate } from '../../../utils/dateUtils';
import { REPORTS_MIN_DATE } from '../common/useReportFilters';
import ReportsCalendarHeader from '../common/ReportsCalendarHeader';

const DATE_PICKER_VIEWS = ['year', 'month', 'day'];

const ExportTradeHistoryModal = ({ 
  open, 
  onClose, 
  defaultExchange = 'NFO',
  defaultStartDate,
  defaultEndDate,
  title = 'Export Trade History',
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();

  // This modal is only ever opened from TradeHistoryDetails.js, which always
  // supplies both dates (the page's current filter range) — so `new Date()`
  // below is just a sensible fallback for reuse elsewhere, never actually hit.
  const [endDate, setEndDate] = useState(defaultEndDate ? new Date(defaultEndDate) : new Date());
  const [startDate, setStartDate] = useState(defaultStartDate ? new Date(defaultStartDate) : new Date());
  const [segment, setSegment] = useState(defaultExchange);

  const [downloadLoading, setDownloadLoading] = useState(false);

  useEffect(() => {
    if (open) {
      // Initialize dates when modal opens
      setEndDate(defaultEndDate ? new Date(defaultEndDate) : new Date());
      setStartDate(defaultStartDate ? new Date(defaultStartDate) : new Date());
      setSegment(defaultExchange);
    }
  }, [open, defaultExchange, defaultStartDate, defaultEndDate]);

  // If endDate is moved to before the current startDate, pull startDate back
  // to 5 days before the new endDate so the range stays valid.
  useEffect(() => {
    if (endDate) {
      if (!startDate || startDate > endDate) {
        const calculatedStartDate = new Date(endDate);
        calculatedStartDate.setDate(calculatedStartDate.getDate() - 5);
        setStartDate(calculatedStartDate);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endDate]);
  
  const handleSegmentChange = (event) => {
    setSegment(event.target.value);
  };
  
  const isDateRangeValid = () => {
    return startDate && endDate && startDate <= endDate;
  };
  
  const handleDownloadCSV = async () => {
    if (!isDateRangeValid()) {
      enqueueSnackbar('Please select a valid date range', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 1000
      });
      return;
    }
    
    setDownloadLoading(true);
    
    try {
      const payload = {
        startDate: formatApiDate(startDate),
        endDate: formatApiDate(endDate),
        exchange: segment,
        info: 'download'
      };
      
      const response = await fetchHistoricalTrades(payload);
      
      // The response should now be a blob directly from primusAPI
      if (response) {
        // Create URL from the blob response
        const url = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `trade_history_${payload.exchange}_${payload.startDate}_to_${payload.endDate}.csv`);
        
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        enqueueSnackbar('Trade history downloaded successfully', { 
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
          autoHideDuration: 1000
        });
        onClose();
      } else {
        throw new Error('Failed to download trade history');
      }
    } catch (error) {
      console.error('Download error:', error);
      enqueueSnackbar(error.message || 'Failed to download trade history', { 
        variant: 'error',
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
        autoHideDuration: 1000
      });
    } finally {
      setDownloadLoading(false);
    }
  };
  
  const handleModalClose = () => {
    setDownloadLoading(false);
    onClose();
  };
  
  return (
    <Modal
      open={open}
      onClose={handleModalClose}
      aria-labelledby="trade-history-modal-title"
    >
      <Paper sx={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: isMobile ? '95%' : 700,
        maxHeight: isMobile ? '90vh' : 'auto',
        overflow: 'auto',
        p: isMobile ? 2 : 4,
        borderRadius: 2,
        outline: 'none',
        boxShadow: 24,
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 3 
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <HistoryIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
            <Typography id="trade-history-modal-title" variant="h6" component="h2" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
          </Box>
          <IconButton 
            onClick={handleModalClose}
            disabled={downloadLoading}
            sx={{ color: theme.palette.text.secondary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        
        <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>
          Select a date range and the market segment to export your trade history.
        </Typography>
        
        <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enLocale}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row', 
            gap: 2, 
            mb: 3 
          }}>
            <DatePicker
              label="From"
              value={startDate}
              minDate={REPORTS_MIN_DATE}
              maxDate={endDate}
              onChange={(newValue) => setStartDate(newValue)}
              format="dd/MM/yyyy"
              views={DATE_PICKER_VIEWS}
              slots={{ calendarHeader: ReportsCalendarHeader }}
              slotProps={{
                textField: { fullWidth: true, InputLabelProps: { style: { fontWeight: 600 } } }
              }}
            />
            <DatePicker
              label="To"
              value={endDate}
              minDate={startDate}
              maxDate={new Date()}
              onChange={(newValue) => setEndDate(newValue)}
              format="dd/MM/yyyy"
              views={DATE_PICKER_VIEWS}
              slots={{ calendarHeader: ReportsCalendarHeader }}
              slotProps={{
                textField: { fullWidth: true, InputLabelProps: { style: { fontWeight: 600 } } }
              }}
            />
          </Box>
        </LocalizationProvider>

        <FormControl fullWidth sx={{ mb: 3 }} size={isMobile ? "small" : "medium"}>
          <InputLabel id="segment-select-label">Market Segment</InputLabel>
          <Select
            labelId="segment-select-label"
            id="segment-select"
            value={segment}
            label="Market Segment"
            onChange={handleSegmentChange}
          >
            <MenuItem value="NSE">Cash NSE</MenuItem>
            <MenuItem value="BSE">Cash BSE</MenuItem>
            <MenuItem value="NFO">Derivative NFO</MenuItem>
            <MenuItem value="BFO">Derivative BFO</MenuItem>
            <MenuItem value="MCX">Commodities</MenuItem>
            <MenuItem value="CDS">Forex</MenuItem>
          </Select>
        </FormControl>
        
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'flex-end', 
          gap: 2,
          '& > button': {
            width: isMobile ? '100%' : 'auto'
          }
        }}>
          <Button
            variant="contained" 
            color="secondary"
            onClick={handleDownloadCSV}
            disabled={downloadLoading || !isDateRangeValid()}
            startIcon={downloadLoading ? <CircularProgress size={20} /> : <DownloadIcon />}
            size={isMobile ? "small" : "medium"}
          >
            {downloadLoading ? 'Downloading...' : 'Download CSV'}
          </Button>
        </Box>
      </Paper>
    </Modal>
  );
};

export default ExportTradeHistoryModal; 